import { apiFetch } from './client';

// Notices (Aviseringar) API client — mirrors internal/api/notices.go.
// Money is in MINOR units (öre / cents). All endpoints are org-admin gated.

export type FlowProfile =
  | 'booking'
  | 'regular_team'
  | 'open_tryout'
  | 'tryout_approval'
  | 'free_group'
  | 'manual';

export type Period = 'spring' | 'autumn' | 'full_year' | 'season' | 'custom';

export type NoticeState =
  | 'draft'
  | 'awaiting_approval'
  | 'declined'
  | 'sent'
  | 'overdue'
  | 'paid'
  | 'deregistered'
  | 'consequence_applied'
  | 'cancelled';

// NoticeConfigValues is one STORED layer (team override or category defaults).
// Every field is optional/nullable — absent/null means "inherit from the layer
// below" (team → category → flow default). This is what the config forms edit.
export interface NoticeConfigValues {
  flow_profile?: FlowProfile | null;
  fee_profile_id?: string | null;
  section?: string | null;
  requires_membership?: boolean | null;
  membership_year?: number | null;
  currency?: string | null;
  tryout_before_payment?: boolean | null;
  tryout_enabled?: boolean | null;
  tryout_length_weeks?: number | null;
  tryout_length_sessions?: number | null;
  self_dereg_enabled?: boolean | null;
  self_dereg_label?: string | null;
  self_dereg_comment_required?: boolean | null;
  seat_limit?: number | null;
  pay_on_booking?: boolean | null;
  reminders_enabled?: boolean | null;
  consequence?: string | null;
}

// ResolvedNoticeConfig is the effective config after merging all layers — every
// field populated. The team editor shows these as the "inherited" placeholders.
export interface ResolvedNoticeConfig {
  team_id: string;
  flow_profile: FlowProfile;
  fee_profile_id?: string;
  section?: string;
  requires_membership: boolean;
  membership_year?: number;
  currency?: string;
  tryout_before_payment: boolean;
  tryout_enabled: boolean;
  tryout_length_weeks?: number;
  tryout_length_sessions?: number;
  self_dereg_enabled: boolean;
  self_dereg_label?: string;
  self_dereg_comment_required: boolean;
  seat_limit?: number;
  pay_on_booking: boolean;
  reminders_enabled: boolean;
  consequence?: string;
}

// TeamNoticeConfig is what GET /config/{teamId} returns: the team's stored
// override values plus the resolved effective config. For the team editor it
// also carries `inherited` — what the team would resolve to with NO overrides of
// its own (category defaults over flow defaults) — so each field can be diffed
// against its category baseline to flag team-specific values. Absent on the
// category-config response (a category has nothing above it to inherit from).
export interface TeamNoticeConfig {
  values: NoticeConfigValues;
  resolved: ResolvedNoticeConfig;
  inherited?: ResolvedNoticeConfig;
}

// FeeReason is one machine-reason entry behind a member's outcome: either a
// charged fee line (owed=true, with an amount) or a deliberately-skipped fee
// kind (owed=false). The SPA phrases these per locale from the reason code.
export interface FeeReason {
  kind: 'membership' | 'training' | 'addon';
  reason: string;
  owed: boolean;
  amount_minor?: number;
  period?: string;
  label?: string;
}

export interface PreviewRow {
  member_id: string;
  member_name: string;
  age?: number;
  total_minor: number;
  would_be_noticed: boolean;
  missing_contact: boolean;
  reasons: FeeReason[];
  // explanation is the English fallback; the UI renders `reasons` instead.
  explanation: string;
}

export interface Preview {
  team_id: string;
  period: string;
  currency: string;
  rows: PreviewRow[];
  total_expected_minor: number;
  notice_count: number;
  fee_free_count: number;
  missing_contact_count: number;
}

export interface SendResult {
  created: string[];
  skipped_no_contact: string[];
  skipped_existing: string[];
  fee_free_count: number;
  total_noticed_minor: number;
}

export interface NoticeSummary {
  id: string;
  member_id: string;
  member_name: string;
  team_id: string;
  period: string;
  season?: string;
  year: number;
  state: NoticeState;
  currency?: string;
  total_minor: number;
  due_date?: string;
  created_at: string;
}

export interface CollectionStatus {
  team_id: string;
  period: string;
  year: number;
  currency: string;
  // The money story (§11.1): expected − lost − outstanding = collected.
  expected_minor: number;
  collected_minor: number;
  lost_minor: number;
  outstanding_minor: number;
  // Outstanding split by reminder-ladder position.
  noticed_minor: number;
  overdue_minor: number;
  sent_count: number;
  overdue_count: number;
  paid_count: number;
  deregistered_count: number;
  consequence_count: number;
  prev_year_collected_minor: number;
  yoy_percent?: number;
  // Forecast range (§11.3): conservative ≤ likely ≤ optimistic.
  forecast_conservative_minor: number;
  forecast_likely_minor: number;
  forecast_optimistic_minor: number;
}

export interface Deregistration {
  notice_id: string;
  member_id: string;
  member_name: string;
  reason?: string;
  comment?: string;
  deregistered_at: string;
}

interface RunSelector {
  team_id: string;
  period: Period;
  season?: string;
  year: number;
  due_date?: string;
  // member_id narrows a send to one member (Members-tab send/resend). Ignored
  // by preview. Empty/absent = the whole team (the season run).
  member_id?: string;
}

const base = (slug: string) =>
  `/api/v1/organizations/${encodeURIComponent(slug)}/notices`;

// Team config: the stored override values + the resolved effective config.
// Never 404s now — an unconfigured team resolves to its category/flow defaults.
export async function getNoticeConfig(
  token: string | null,
  slug: string,
  teamId: string,
): Promise<TeamNoticeConfig> {
  return apiFetch<TeamNoticeConfig>(`${base(slug)}/config/${teamId}`, { token });
}

export async function saveNoticeConfig(
  token: string | null,
  slug: string,
  teamId: string,
  values: NoticeConfigValues,
): Promise<TeamNoticeConfig> {
  return apiFetch<TeamNoticeConfig>(`${base(slug)}/config/${teamId}`, {
    method: 'PUT',
    token,
    body: values,
  });
}

// Category defaults layer. Returns the stored values plus a `resolved` view of
// the flow-profile defaults the inherit fields fall back to (so the editor can
// show concrete default hints).
export async function getCategoryNoticeConfig(
  token: string | null,
  slug: string,
  categoryId: string,
): Promise<TeamNoticeConfig> {
  return apiFetch<TeamNoticeConfig>(`${base(slug)}/category-config/${categoryId}`, { token });
}

export async function saveCategoryNoticeConfig(
  token: string | null,
  slug: string,
  categoryId: string,
  values: NoticeConfigValues,
): Promise<NoticeConfigValues> {
  return apiFetch<NoticeConfigValues>(`${base(slug)}/category-config/${categoryId}`, {
    method: 'PUT',
    token,
    body: values,
  });
}

export async function previewNoticeRun(
  token: string | null,
  slug: string,
  sel: RunSelector,
): Promise<Preview> {
  return apiFetch<Preview>(`${base(slug)}/preview`, { method: 'POST', token, body: sel });
}

export async function sendNoticeRun(
  token: string | null,
  slug: string,
  sel: RunSelector,
): Promise<SendResult> {
  return apiFetch<SendResult>(`${base(slug)}/send`, { method: 'POST', token, body: sel });
}

export async function listNotices(
  token: string | null,
  slug: string,
  opts: { team_id?: string; state?: NoticeState; period?: string; year?: number } = {},
): Promise<{ notices: NoticeSummary[] }> {
  const qs = new URLSearchParams();
  if (opts.team_id) qs.set('team_id', opts.team_id);
  if (opts.state) qs.set('state', opts.state);
  if (opts.period) qs.set('period', opts.period);
  if (opts.year !== undefined) qs.set('year', String(opts.year));
  const q = qs.toString();
  return apiFetch(`${base(slug)}${q ? `?${q}` : ''}`, { token });
}

// TeamNoticeStat is one row of the Teams overview: per-team notice aggregates
// across all periods. Only teams with at least one notice are returned; the
// page merges these against the full team list so unstarted teams still show.
export interface TeamNoticeStat {
  team_id: string;
  notice_count: number;
  outstanding_count: number;
  paid_count: number;
  last_activity?: string;
}

export async function listTeamNoticeOverview(
  token: string | null,
  slug: string,
): Promise<{ teams: TeamNoticeStat[] }> {
  return apiFetch(`${base(slug)}/teams-overview`, { token });
}

export async function getCollectionStatus(
  token: string | null,
  slug: string,
  teamId: string,
  period: string,
  year: number,
): Promise<CollectionStatus> {
  const qs = new URLSearchParams({ team_id: teamId, period, year: String(year) });
  return apiFetch<CollectionStatus>(`${base(slug)}/collection?${qs.toString()}`, { token });
}

export interface SubscriptionMetrics {
  active_count: number;
  mrr_minor: number;
  projected_next_charge_minor: number;
  churn_last_30_count: number;
  currency: string;
}

export async function getSubscriptionMetrics(
  token: string | null,
  slug: string,
  teamId: string,
): Promise<SubscriptionMetrics> {
  const qs = new URLSearchParams({ team_id: teamId });
  return apiFetch<SubscriptionMetrics>(`${base(slug)}/subscriptions/metrics?${qs.toString()}`, {
    token,
  });
}

export interface Subscription {
  id: string;
  team_id: string;
  member_id: string;
  member_name: string;
  plan_kind: string;
  status: string;
  amount_minor: number;
  membership_minor: number;
  currency: string;
  anchor_day: number;
  cycles_charged: number;
  installments_total?: number;
  next_charge_on?: string;
  first_charge_done: boolean;
  // When the member agreed that this card may be charged automatically each
  // period. Absent = no mandate yet, so cycles bill as ordinary invoices rather
  // than being collected silently, and the card-save flow asks for agreement.
  mandate_accepted_at?: string;
}

export async function listSubscriptions(
  token: string | null,
  slug: string,
  opts: { team_id?: string; status?: string } = {},
): Promise<{ subscriptions: Subscription[] }> {
  const qs = new URLSearchParams();
  if (opts.team_id) qs.set('team_id', opts.team_id);
  if (opts.status) qs.set('status', opts.status);
  const q = qs.toString();
  return apiFetch(`${base(slug)}/subscriptions${q ? `?${q}` : ''}`, { token });
}

export async function cancelSubscription(
  token: string | null,
  slug: string,
  subId: string,
  reason?: string,
): Promise<void> {
  await apiFetch(`${base(slug)}/subscriptions/${subId}/cancel`, {
    method: 'POST',
    token,
    body: { reason: reason ?? '' },
  });
}

export interface CardSetupInit {
  client_secret: string;
  setup_intent_id: string;
  stripe_account_id: string;
}

// startCardSetup begins saving a card for off-session recurring charges.
//
// mandateAccepted asserts that the member has agreed to be charged automatically
// each period. The server refuses (422 mandate_required) without it and records
// the consent itself — which wording, when, and from where — so the acceptance is
// evidence we can stand behind rather than a client-supplied claim. Already-
// mandated subscriptions (re-saving an expired card) don't need it again.
export async function startCardSetup(
  token: string | null,
  slug: string,
  subId: string,
  mandateAccepted: boolean,
): Promise<CardSetupInit> {
  return apiFetch(`${base(slug)}/subscriptions/${subId}/card-setup`, {
    method: 'POST',
    token,
    body: { mandate_accepted: mandateAccepted },
  });
}

export async function listDeregistrations(
  token: string | null,
  slug: string,
  teamId: string,
  period: string,
  year: number,
): Promise<{ deregistrations: Deregistration[] }> {
  const qs = new URLSearchParams({ team_id: teamId, period, year: String(year) });
  return apiFetch(`${base(slug)}/deregistrations?${qs.toString()}`, { token });
}

export async function deregisterNotice(
  token: string | null,
  slug: string,
  noticeId: string,
  body: { reason?: string; comment?: string; scope?: string },
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/${noticeId}/deregister`, { method: 'POST', token, body });
}

export async function markNoticePaid(
  token: string | null,
  slug: string,
  noticeId: string,
  invoiceId?: string,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/${noticeId}/pay`, {
    method: 'POST',
    token,
    body: invoiceId ? { invoice_id: invoiceId } : {},
  });
}

export async function cancelNotice(
  token: string | null,
  slug: string,
  noticeId: string,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/${noticeId}/cancel`, { method: 'POST', token });
}

// --- Flow 3: try-outs (docs/notices-rebuild-go-plan.md §2) ---

export interface CreateTryoutInput {
  team_id: string;
  member_id: string;
  period: Period;
  season?: string;
  year: number;
  due_date?: string;
  require_approval: boolean;
  mode?: 'weeks' | 'sessions';
  length?: number;
}

export interface PendingApproval {
  notice_id: string;
  member_id: string;
  member_name: string;
  team_id: string;
  requested_at: string;
}

export async function createTryout(
  token: string | null,
  slug: string,
  body: CreateTryoutInput,
): Promise<{ notice_id: string }> {
  return apiFetch(`${base(slug)}/tryout`, { method: 'POST', token, body });
}

export async function approveTryout(
  token: string | null,
  slug: string,
  noticeId: string,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/${noticeId}/approve`, { method: 'POST', token });
}

export async function declineTryout(
  token: string | null,
  slug: string,
  noticeId: string,
  note?: string,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/${noticeId}/decline`, {
    method: 'POST',
    token,
    body: note ? { note } : {},
  });
}

export async function listPendingApprovals(
  token: string | null,
  slug: string,
): Promise<{ pending: PendingApproval[]; total: number }> {
  return apiFetch(`${base(slug)}/approvals/pending`, { token });
}

// --- Flow 1: booking with seats (docs/notices-rebuild-go-plan.md §2) ---

export interface BookInput {
  team_id: string;
  member_id: string;
  period: Period;
  season?: string;
  year: number;
  due_date?: string;
}

export interface BookResult {
  booked: boolean;
  waitlisted: boolean;
  notice_id?: string;
  seat_id?: string;
  position?: number;
}

export interface SeatSummary {
  seat_limit: number;
  held: number;
  confirmed: number;
  waiting: number;
  available: number;
}

export interface SeatBooking {
  seat_id: string;
  member_id: string;
  member_name: string;
  status: 'held' | 'confirmed';
}

export interface SeatWatcher {
  member_id: string;
  member_name: string;
  position: number;
}

export interface SeatList {
  bookings: SeatBooking[];
  watchers: SeatWatcher[];
}

export async function bookSeat(
  token: string | null,
  slug: string,
  body: BookInput,
): Promise<BookResult> {
  return apiFetch(`${base(slug)}/booking`, { method: 'POST', token, body });
}

export async function releaseSeat(
  token: string | null,
  slug: string,
  seatId: string,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/seats/${seatId}/release`, { method: 'POST', token });
}

export async function getSeatSummary(
  token: string | null,
  slug: string,
  teamId: string,
  period: string,
  year: number,
): Promise<SeatSummary> {
  const qs = new URLSearchParams({ team_id: teamId, period, year: String(year) });
  return apiFetch<SeatSummary>(`${base(slug)}/seats/summary?${qs.toString()}`, { token });
}

export async function listSeats(
  token: string | null,
  slug: string,
  teamId: string,
  period: string,
  year: number,
): Promise<SeatList> {
  const qs = new URLSearchParams({ team_id: teamId, period, year: String(year) });
  return apiFetch<SeatList>(`${base(slug)}/seats?${qs.toString()}`, { token });
}

// --- Fee authoring: profiles + rules (amounts the engine resolves) ---

export type FeeRuleLevel = 'club' | 'section' | 'group' | 'person' | 'override';

export interface FeeProfile {
  id: string;
  name: string;
  flow_profile: FlowProfile;
  description?: string;
  is_seed: boolean;
}

export interface FeeProfileInput {
  name: string;
  flow_profile?: FlowProfile;
  description?: string | null;
}

// FeeRule mirrors notices.FeeRule. Amounts are MINOR units. Every scope /
// condition field is optional (null/absent = wildcard at that dimension).
export interface FeeRule {
  id: string;
  fee_profile_id?: string | null;
  level: FeeRuleLevel;
  section?: string | null;
  team_id?: string | null;
  member_id?: string | null;
  min_age?: number | null;
  max_age?: number | null;
  birth_year?: number | null;
  role?: string | null;
  member_type?: string | null;
  tryout_only?: boolean | null;
  membership_minor?: number | null;
  training_minor?: number | null;
  addon_minor?: number | null;
  label?: string | null;
  exempt_membership: boolean;
  exempt_training: boolean;
  period?: string | null;
  reason?: string | null;
  note?: string | null;
  currency?: string | null;
}

// FeeRuleInput is the writable payload (FeeRule minus the id).
export type FeeRuleInput = Omit<FeeRule, 'id'>;

export async function listFeeProfiles(
  token: string | null,
  slug: string,
): Promise<{ fee_profiles: FeeProfile[] }> {
  return apiFetch(`${base(slug)}/fee-profiles`, { token });
}

export async function createFeeProfile(
  token: string | null,
  slug: string,
  body: FeeProfileInput,
): Promise<{ id: string }> {
  return apiFetch(`${base(slug)}/fee-profiles`, { method: 'POST', token, body });
}

export async function updateFeeProfile(
  token: string | null,
  slug: string,
  id: string,
  body: FeeProfileInput,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/fee-profiles/${id}`, { method: 'PATCH', token, body });
}

export async function deleteFeeProfile(
  token: string | null,
  slug: string,
  id: string,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/fee-profiles/${id}`, { method: 'DELETE', token });
}

export async function listFeeRules(
  token: string | null,
  slug: string,
  profileId?: string,
): Promise<{ fee_rules: FeeRule[] }> {
  const qs = profileId ? `?profile_id=${encodeURIComponent(profileId)}` : '';
  return apiFetch(`${base(slug)}/fee-rules${qs}`, { token });
}

export async function createFeeRule(
  token: string | null,
  slug: string,
  body: FeeRuleInput,
): Promise<{ id: string }> {
  return apiFetch(`${base(slug)}/fee-rules`, { method: 'POST', token, body });
}

export async function updateFeeRule(
  token: string | null,
  slug: string,
  id: string,
  body: FeeRuleInput,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/fee-rules/${id}`, { method: 'PATCH', token, body });
}

export async function deleteFeeRule(
  token: string | null,
  slug: string,
  id: string,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/fee-rules/${id}`, { method: 'DELETE', token });
}

// --- Category fees (everyday authoring path) + club membership fee ---

// getCategoryFees ensures the category's implicit fee profile exists and returns
// it with that category's own training rules (excludes the club membership rule).
export async function getCategoryFees(
  token: string | null,
  slug: string,
  categoryId: string,
): Promise<{ fee_profile_id: string; fee_rules: FeeRule[] }> {
  return apiFetch(`${base(slug)}/category-fees/${categoryId}`, { token });
}

export interface ClubMembershipFee {
  membership_minor?: number | null;
  currency?: string | null;
}

export async function getClubMembershipFee(
  token: string | null,
  slug: string,
): Promise<ClubMembershipFee> {
  return apiFetch(`${base(slug)}/club-membership-fee`, { token });
}

export async function setClubMembershipFee(
  token: string | null,
  slug: string,
  body: { membership_minor: number; currency?: string | null },
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/club-membership-fee`, { method: 'PUT', token, body });
}

// --- Batch notice runs (category / org-wide fee sends) ---

export type BatchScope = 'team' | 'category' | 'org';

export interface BatchRunRequest {
  scope: BatchScope;
  team_id?: string;
  category_id?: string;
  period: Period;
  season?: string;
  year: number;
  due_date?: string;
}

// ZeroReason explains why a team in scope produces no notices (mirrors the
// backend ZeroReason* constants). Empty string when the team does owe.
export type ZeroReason = '' | 'no_athletes' | 'all_fee_free';

// BatchTeamStat is one team's slice of the batch preview. `owing` splits
// exactly into new_notices + already_billed + missing_contact (send priority).
export interface BatchTeamStat {
  team_id: string;
  team_name: string;
  athletes: number;
  owing: number;
  new_notices: number;
  already_billed: number;
  missing_contact: number;
  total_minor: number;
  zero_reason: ZeroReason;
}

export interface BatchPreview {
  teams_in_scope: number;
  teams_with_notices: number;
  notice_count: number; // gross owing across all teams
  new_notices: number; // will actually be created + sent
  already_billed: number; // owe but already noticed this period (send skips)
  missing_contact: number; // owe but unreachable (send skips)
  total_expected_minor: number; // gross owed
  new_total_minor: number; // amount the send will actually bill
  currency: string;
  teams: BatchTeamStat[];
}

export type NoticeRunStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface NoticeRunView {
  run_id: string;
  scope: BatchScope;
  status: NoticeRunStatus;
  progress: {
    teams_total: number;
    teams_processed: number;
    notices_created: number;
    total_minor: number;
    skipped_no_contact: number;
    skipped_no_fee: number;
  };
  error_message?: string;
}

export async function previewNoticeBatch(
  token: string | null,
  slug: string,
  req: BatchRunRequest,
): Promise<BatchPreview> {
  return apiFetch(`${base(slug)}/preview-batch`, { method: 'POST', token, body: req });
}

export async function enqueueNoticeBatch(
  token: string | null,
  slug: string,
  req: BatchRunRequest,
): Promise<{ run_id: string; status: NoticeRunStatus }> {
  return apiFetch(`${base(slug)}/runs`, { method: 'POST', token, body: req });
}

export async function getNoticeRun(
  token: string | null,
  slug: string,
  runId: string,
): Promise<NoticeRunView> {
  return apiFetch(`${base(slug)}/runs/${runId}`, { token });
}

export async function cancelNoticeRun(
  token: string | null,
  slug: string,
  runId: string,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/runs/${runId}/cancel`, { method: 'POST', token });
}

// --- Member-centric worklist (Notices → Members tab) ---

// MemberLineStatus is the derived per-line status powering the Members-tab
// filter tabs. Mirrors the backend ML* constants.
export type MemberLineStatus =
  | 'paid'
  | 'noticed'
  | 'overdue'
  | 'tryout'
  | 'deregistered'
  | 'cancelled'
  | 'to_notice'
  | 'fee_free';

export interface MemberNoticeLine {
  member_id: string;
  member_name: string;
  team_id: string;
  team_name: string;
  amount_minor: number;
  currency?: string;
  status: MemberLineStatus;
  notice_id?: string;
  notice_date?: string;
  due_date?: string;
  missing_contact: boolean;
}

export interface MemberNoticeLines {
  rows: MemberNoticeLine[];
  total: number;
  // counts keys: 'all' + each MemberLineStatus (over the search-filtered set).
  counts: Record<string, number>;
  currency: string;
}

export interface MemberLinesQuery {
  period: Period;
  year: number;
  season?: string;
  category_id?: string;
  team_id?: string;
  // Multi-select status filter (ML* statuses). Empty/absent = all.
  statuses?: string[];
  q?: string;
  limit?: number;
  offset?: number;
}

export async function listMemberNoticeLines(
  token: string | null,
  slug: string,
  query: MemberLinesQuery,
): Promise<MemberNoticeLines> {
  const qs = new URLSearchParams({ period: query.period, year: String(query.year) });
  if (query.season) qs.set('season', query.season);
  if (query.category_id) qs.set('category_id', query.category_id);
  if (query.team_id) qs.set('team_id', query.team_id);
  const statuses = (query.statuses ?? []).filter((s) => s && s !== 'all');
  if (statuses.length > 0) qs.set('status', statuses.join(','));
  if (query.q) qs.set('q', query.q);
  if (query.limit !== undefined) qs.set('limit', String(query.limit));
  if (query.offset !== undefined) qs.set('offset', String(query.offset));
  return apiFetch<MemberNoticeLines>(`${base(slug)}/member-lines?${qs.toString()}`, { token });
}

// sendMemberNotice sends (or resends) a notice to a single member for a period.
// Idempotent server-side: a member who already has a live notice is skipped.
export async function sendMemberNotice(
  token: string | null,
  slug: string,
  sel: RunSelector & { member_id: string },
): Promise<SendResult> {
  return apiFetch<SendResult>(`${base(slug)}/send`, { method: 'POST', token, body: sel });
}
